import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, IconButton, makeStyles, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory, useLocation } from "react-router-dom";
import { useStores } from "../../hooks/use-stores";
import services from "../../services";
import { getTicketEventsNavPaths } from "./ticketEventsNavPaths";
import { normalizeHexColor } from "./ticketProofUtils";

const FALLBACK_BG = "#2ecc71";

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    color: "#000",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  stage: {
    position: "relative",
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  surface: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
    color: "#000",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  surfacePortrait: {
    top: 0,
    right: "auto",
    bottom: "auto",
    left: "100vw",
    width: "100vh",
    height: "100vw",
    transformOrigin: "top left",
    transform: "rotate(90deg)",
  },
  closeBtnWrap: {
    position: "absolute",
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    zIndex: 5,
    pointerEvents: "auto",
  },
  closeBtn: {
    color: "#000",
    backgroundColor: "rgba(255,255,255,0.55)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.75)",
    },
  },
  main: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 3, 3),
    minHeight: 0,
  },
  centerBlock: {
    textAlign: "center",
    maxWidth: 1200,
    width: "100%",
  },
  name: {
    margin: 0,
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 700,
    fontSize: "clamp(2rem, 9vw, 5rem)",
    letterSpacing: "0.06em",
    lineHeight: 1.1,
    textTransform: "uppercase",
  },
  subline: {
    marginTop: theme.spacing(2),
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 400,
    fontSize: "clamp(1rem, 3.5vw, 2rem)",
    letterSpacing: "0.02em",
  },
}));

function ProofOfTicket({ holderName, eventLine, onClose }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { authStore } = useStores();
  const [orientation, setOrientation] = useState(() => {
    if (typeof window === "undefined") return "landscape";
    return window.matchMedia &&
      window.matchMedia("(orientation: portrait)").matches
      ? "portrait"
      : "landscape";
  });
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    services.Events.ticketProofSettings()
      .then((res) => {
        if (!cancelled) setSettings(res || {});
      })
      .catch(() => {
        if (!cancelled) setSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(orientation: portrait)");
    const handler = (event) =>
      setOrientation(event.matches ? "portrait" : "landscape");
    handler(mq);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflowX = html.style.overflowX;
    const prevBodyOverflowX = body.style.overflowX;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overflowX = prevHtmlOverflowX;
      body.style.overflowX = prevBodyOverflowX;
    };
  }, []);

  const bgColor = useMemo(() => {
    return (
      normalizeHexColor(settings?.bgColor) ||
      normalizeHexColor(settings?.BgColor) ||
      FALLBACK_BG
    );
  }, [settings]);

  // Paint the body background too so any sliver outside the rotated surface
  // (e.g. address bar reveal on mobile) stays on theme.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevBody = body.style.backgroundColor;
    const prevHtml = html.style.backgroundColor;
    body.style.backgroundColor = bgColor;
    html.style.backgroundColor = bgColor;
    return () => {
      body.style.backgroundColor = prevBody;
      html.style.backgroundColor = prevHtml;
    };
  }, [bgColor]);

  const computedName = useMemo(() => {
    if (holderName) return holderName;
    const user = authStore?.currentUser;
    if (!user) return "";
    const first = user.firstName || "";
    const last = user.lastName || "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    return user.name || user.displayName || "";
  }, [holderName, authStore?.currentUser]);

  const computedEventLine = useMemo(() => {
    if (eventLine) return eventLine;
    return settings?.eventLine ?? "";
  }, [eventLine, settings]);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    const paths = getTicketEventsNavPaths(location.pathname);
    if (paths?.ticketHolder) {
      history.push(paths.ticketHolder);
      return;
    }
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/");
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // We intentionally exclude handleClose from deps; it closes over stable
    // refs (history/onClose) and we want a single listener for the component
    // lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const surfaceClassName = `${classes.surface} ${
    orientation === "portrait" ? classes.surfacePortrait : ""
  }`;

  return (
    <div className={classes.overlay} style={{ backgroundColor: bgColor }}>
      <div className={classes.stage}>
        <div className={surfaceClassName} style={{ backgroundColor: bgColor }}>
          <div className={classes.closeBtnWrap}>
            <IconButton
              aria-label="Close proof of ticket"
              className={classes.closeBtn}
              onClick={handleClose}
              size="medium"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Box component="main" className={classes.main}>
            <div className={classes.centerBlock}>
              <Typography component="h1" className={classes.name}>
                {computedName}
              </Typography>
              {computedEventLine ? (
                <Typography component="p" className={classes.subline}>
                  {computedEventLine}
                </Typography>
              ) : null}
            </div>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default observer(ProofOfTicket);
